<template>
  <v-container fluid class="analise-page pa-4 pa-md-6">
    <!-- Header com botao de importar -->
    <v-row class="mb-4">
      <v-col cols="12" class="d-flex align-center justify-space-between">
        <div class="d-flex align-center">
          <v-icon color="primary" size="32" class="mr-3">mdi-chart-timeline-variant</v-icon>
          <div>
            <div class="headline font-weight-bold" style="color: #0E314A">
              Analise de Cadastro Historico
            </div>
            <div class="caption grey--text">
              Importe bases anteriores e analise padroes de cadastro por assessor
            </div>
          </div>
        </div>
        <v-btn
          color="primary"
          depressed
          class="btn-novo"
          @click="dialogImportar = true"
        >
          <v-icon left small>mdi-upload</v-icon>
          Importar Base
        </v-btn>
      </v-col>
    </v-row>

    <!-- Card de Alerta: Assessores sem Vínculo -->
    <v-alert
      v-if="totalPendentes > 0"
      type="warning"
      colored-border
      border="left"
      elevation="1"
      class="mb-4"
    >
      <v-row align="center" no-gutters>
        <v-col class="grow">
          <div class="subtitle-2 font-weight-bold">
            {{ totalPendentes }} assessor(es) sem vínculo identificado
          </div>
          <div class="caption" v-if="totalPessoasBloqueadas > 0">
            {{ totalPessoasBloqueadas }} registro(s) de pessoas não entram em nenhuma análise enquanto o vínculo não for resolvido
          </div>
          <div class="caption grey--text" v-else>
            Calculando impacto...
          </div>
        </v-col>
        <v-col class="shrink pl-4">
          <v-btn color="warning" depressed small @click="abrirGerenciarVinculos">
            <v-icon left small>mdi-link-variant-plus</v-icon>
            Gerenciar Vínculos
          </v-btn>
        </v-col>
      </v-row>
    </v-alert>

    <!-- Bases Importadas -->
    <v-card outlined class="table-card mb-6">
      <v-card-title class="pa-4">
        <v-icon color="grey" size="22" class="mr-2">mdi-database</v-icon>
        <span class="subtitle-1 font-weight-bold grey--text text--darken-1">
          Bases Importadas
        </span>
        <v-spacer></v-spacer>
        <v-chip
          v-if="totalPendentes > 0"
          color="warning"
          small
          class="mr-2"
          style="cursor: pointer"
          @click="abrirGerenciarVinculos"
        >
          <v-icon x-small left>mdi-alert</v-icon>
          {{ totalPendentes }} assessor(es) pendente(s)
        </v-chip>
      </v-card-title>
      <v-data-table
        :headers="headersBases"
        :items="bases"
        :loading="loadingBases"
        loading-text="Carregando bases..."
        class="modern-table"
        :items-per-page="5"
      >
        <template v-slot:item.assessores="{ item }">
          <v-chip
            small
            :color="item.assessores_nao_identificados > 0 ? 'warning' : 'success'"
          >
            {{ item.assessores_identificados }}/{{ item.assessores_identificados + item.assessores_nao_identificados }}
          </v-chip>
        </template>
        <template v-slot:item.actions="{ item }">
          <v-tooltip bottom v-if="item.assessores_nao_identificados > 0">
            <template v-slot:activator="{ on }">
              <v-btn
                icon
                small
                class="action-icon-btn mr-1"
                v-on="on"
                @click="abrirVinculacao(item)"
              >
                <v-icon small color="warning">mdi-link-variant</v-icon>
              </v-btn>
            </template>
            <span>Vincular Assessores</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-btn
                icon
                small
                class="action-icon-btn"
                v-on="on"
                @click="confirmarExclusao(item)"
              >
                <v-icon small color="red lighten-1">mdi-delete</v-icon>
              </v-btn>
            </template>
            <span>Remover Base</span>
          </v-tooltip>
        </template>
        <template v-slot:no-data>
          <div class="text-center pa-10">
            <v-icon size="64" color="grey lighten-2" class="mb-4">mdi-database-off</v-icon>
            <div class="subtitle-1 grey--text">Nenhuma base importada</div>
            <div class="caption grey--text">Clique em "Importar Base" para comecar</div>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <!-- Comparar Bases -->
    <v-row v-if="bases.length >= 1" class="mb-6">
      <v-col cols="12">
        <v-card outlined class="table-card">
          <v-card-title class="pa-4">
            <v-icon color="grey" size="22" class="mr-2">mdi-compare-horizontal</v-icon>
            <span class="subtitle-1 font-weight-bold grey--text text--darken-1">
              Comparar Bases
            </span>
            <v-chip
              v-if="!loadingComparacaoBases && (resumoComparacaoExibido.total_revisar || 0) > 0"
              small color="amber darken-2" dark class="ml-3"
            >
              <v-icon x-small left>mdi-help-circle</v-icon>
              {{ resumoComparacaoExibido.total_revisar }} a revisar
            </v-chip>
            <v-chip v-if="filtroEquipeComparacao !== null" small color="indigo" dark class="ml-3" close @click:close="filtroEquipeComparacao = null">
              <v-icon x-small left>mdi-account-group</v-icon>
              {{ filtroEquipeComparacao }}
            </v-chip>
            <v-chip v-if="filtroAssessorComparacao !== null" small color="primary" class="ml-3" close @click:close="filtroAssessorComparacao = null">
              <v-icon x-small left>mdi-account</v-icon>
              {{ nomeAssessorFiltrado }}
            </v-chip>
            <span v-if="filtroEquipeComparacao === null && filtroAssessorComparacao === null" class="caption grey--text ml-3 d-none d-sm-inline">
              Verifique se a carteira de pessoas esta sendo renovada ou apenas repetida entre duas bases
            </span>
          </v-card-title>

          <!-- Seletores -->
          <v-row dense class="px-4 pt-2 align-center">
            <v-col cols="12" sm="5">
              <v-select
                v-model="comparar.baseA"
                :items="basesParaComparar"
                outlined dense
                label="Base de origem (A)"
                prepend-inner-icon="mdi-database-arrow-right"
                class="styled-select"
                hide-details
                @change="carregarComparacaoBases"
              ></v-select>
            </v-col>
            <v-col cols="12" sm="2" class="text-center">
              <v-btn small text color="primary" @click="inverterBases">
                <v-icon small left>mdi-swap-horizontal</v-icon>
                Inverter
              </v-btn>
            </v-col>
            <v-col cols="12" sm="5">
              <v-select
                v-model="comparar.baseB"
                :items="basesParaComparar"
                outlined dense
                label="Base de destino (B)"
                prepend-inner-icon="mdi-database-arrow-left"
                class="styled-select"
                hide-details
                @change="carregarComparacaoBases"
              ></v-select>
            </v-col>
          </v-row>

          <v-alert
            v-if="comparar.baseA && comparar.baseB && comparar.baseA === comparar.baseB"
            type="info" text dense class="mx-4 mt-4 mb-0"
          >
            Selecione duas bases diferentes para comparar.
          </v-alert>

          <!-- Filtros de equipe e assessor -->
          <v-row dense class="px-4 pt-3 align-center" v-if="porAssessorComparacao.length > 0">
            <v-col cols="12" sm="6" md="4">
              <v-autocomplete
                v-model="filtroEquipeComparacao"
                :items="opcoesEquipeComparacao"
                outlined dense clearable hide-details
                label="Filtrar por equipe"
                prepend-inner-icon="mdi-account-group"
                class="styled-select"
                no-data-text="Nenhuma equipe"
                @change="filtroAssessorComparacao = null"
              ></v-autocomplete>
            </v-col>
            <v-col cols="12" sm="6" md="4">
              <v-autocomplete
                v-model="filtroAssessorComparacao"
                :items="opcoesAssessorComparacao"
                outlined dense clearable hide-details
                label="Filtrar por assessor"
                prepend-inner-icon="mdi-account-search"
                class="styled-select"
                no-data-text="Nenhum assessor"
              ></v-autocomplete>
            </v-col>
            <v-col cols="12" md="4" class="d-flex align-center">
              <v-btn
                v-if="podeConfirmarRevisao && revisarAltaNoEscopo.length > 0"
                small color="success" depressed
                :loading="confirmandoLote"
                @click="confirmarRevisaoLote"
              >
                <v-icon small left>mdi-check-all</v-icon>
                Confirmar pares ≥ 95% ({{ revisarAltaNoEscopo.length }})
              </v-btn>
            </v-col>
          </v-row>

          <!-- Painel de detalhamento (assessor ou equipe) -->
          <v-row v-if="detalheExibido" dense class="px-4 pt-3">
            <v-col cols="12">
              <v-card outlined color="grey lighten-5" class="pa-3">
                <div class="d-flex flex-wrap align-center" style="gap: 24px;">
                  <div class="d-flex align-center">
                    <v-icon color="primary" class="mr-2">{{ detalheExibido.tipo === 'equipe' ? 'mdi-account-group' : 'mdi-account-tie' }}</v-icon>
                    <div>
                      <div class="caption grey--text text-uppercase">Equipe</div>
                      <div class="subtitle-2 font-weight-bold">
                        {{ detalheExibido.equipe_nome || '—' }}
                        <span v-if="detalheExibido.tipo === 'equipe'" class="caption grey--text ml-1">({{ detalheExibido.n_assessores }} assessores)</span>
                      </div>
                    </div>
                  </div>
                  <v-divider vertical></v-divider>
                  <div class="d-flex align-center">
                    <v-icon color="primary" class="mr-2">mdi-target</v-icon>
                    <div>
                      <div class="caption grey--text text-uppercase">Meta{{ detalheExibido.tipo === 'equipe' ? ' da equipe' : '' }}</div>
                      <div class="subtitle-2 font-weight-bold">
                        <span v-if="detalheExibido.meta_valor !== null && detalheExibido.meta_valor !== undefined">
                          {{ detalheExibido.meta_valor.toLocaleString() }} pessoas
                        </span>
                        <span v-else class="grey--text">—</span>
                        <span v-if="detalheExibido.meta_descricao" class="caption grey--text ml-1">
                          ({{ detalheExibido.meta_descricao }})
                        </span>
                      </div>
                    </div>
                  </div>
                  <v-divider vertical></v-divider>
                  <div class="d-flex align-center">
                    <v-icon color="primary" class="mr-2">mdi-flag-checkered</v-icon>
                    <div>
                      <div class="caption grey--text text-uppercase">Atingiu</div>
                      <v-chip v-if="detalheExibido.pct_meta !== null && detalheExibido.pct_meta !== undefined" small :color="metaColor(detalheExibido.pct_meta)" dark>
                        {{ detalheExibido.pct_meta }}%
                        <span class="ml-1 caption">({{ detalheExibido.total_b.toLocaleString() }}/{{ detalheExibido.meta_valor.toLocaleString() }})</span>
                      </v-chip>
                      <span v-else class="caption grey--text">sem meta cadastrada</span>
                    </div>
                  </div>
                  <v-divider vertical></v-divider>
                  <div class="d-flex align-center">
                    <v-icon color="primary" class="mr-2">mdi-refresh</v-icon>
                    <div>
                      <div class="caption grey--text text-uppercase">Renovação</div>
                      <div>
                        <v-chip v-if="detalheExibido.pct_renovacao !== null && detalheExibido.pct_renovacao !== undefined" small :color="getPctRenovacaoColor(detalheExibido.pct_renovacao)" dark>
                          {{ detalheExibido.pct_renovacao }}%
                        </v-chip>
                        <v-chip small :color="classificacaoColor(detalheExibido.classificacao)" dark class="ml-1">
                          {{ classificacaoLabel(detalheExibido.classificacao) }}
                        </v-chip>
                      </div>
                    </div>
                  </div>
                </div>
              </v-card>
            </v-col>
          </v-row>

          <!-- Cards de resumo -->
          <v-row dense class="px-4 pt-4 pb-2">
            <v-col cols="6" sm="4" md="2">
              <v-card class="kpi-card pa-3" outlined>
                <v-skeleton-loader v-if="loadingComparacaoBases" type="list-item-two-line"></v-skeleton-loader>
                <div v-else>
                  <p class="mb-1 text-uppercase caption grey--text">Total em A</p>
                  <div class="title font-weight-bold">{{ resumoComparacaoExibido.total_a.toLocaleString() }}</div>
                </div>
              </v-card>
            </v-col>
            <v-col cols="6" sm="4" md="2">
              <v-card class="kpi-card pa-3" outlined>
                <v-skeleton-loader v-if="loadingComparacaoBases" type="list-item-two-line"></v-skeleton-loader>
                <div v-else>
                  <p class="mb-1 text-uppercase caption grey--text">Total em B</p>
                  <div class="title font-weight-bold">{{ resumoComparacaoExibido.total_b.toLocaleString() }}</div>
                </div>
              </v-card>
            </v-col>
            <v-col cols="6" sm="4" md="2">
              <v-card class="kpi-card pa-3" outlined>
                <v-skeleton-loader v-if="loadingComparacaoBases" type="list-item-two-line"></v-skeleton-loader>
                <div v-else>
                  <p class="mb-1 text-uppercase caption grey--text">Repetidas (A e B)</p>
                  <div class="title font-weight-bold warning--text">{{ resumoComparacaoExibido.repetidas.toLocaleString() }}</div>
                </div>
              </v-card>
            </v-col>
            <v-col cols="6" sm="4" md="2">
              <v-card class="kpi-card pa-3" outlined>
                <v-skeleton-loader v-if="loadingComparacaoBases" type="list-item-two-line"></v-skeleton-loader>
                <div v-else>
                  <p class="mb-1 text-uppercase caption grey--text">Novas em B</p>
                  <div class="title font-weight-bold success--text">{{ resumoComparacaoExibido.novas_em_b.toLocaleString() }}</div>
                </div>
              </v-card>
            </v-col>
            <v-col cols="6" sm="4" md="2">
              <v-card class="kpi-card pa-3" outlined>
                <v-skeleton-loader v-if="loadingComparacaoBases" type="list-item-two-line"></v-skeleton-loader>
                <div v-else>
                  <p class="mb-1 text-uppercase caption grey--text">Sairam de A</p>
                  <div class="title font-weight-bold grey--text text--darken-1">{{ resumoComparacaoExibido.saiu_de_a.toLocaleString() }}</div>
                </div>
              </v-card>
            </v-col>
            <v-col cols="6" sm="4" md="2">
              <v-card class="kpi-card pa-3" outlined>
                <v-skeleton-loader v-if="loadingComparacaoBases" type="list-item-two-line"></v-skeleton-loader>
                <div v-else>
                  <p class="mb-1 text-uppercase caption grey--text">% Renovacao</p>
                  <v-chip :color="getPctRenovacaoColor(resumoComparacaoExibido.pct_renovacao_global)" dark label>
                    <span class="font-weight-bold">{{ resumoComparacaoExibido.pct_renovacao_global }}%</span>
                  </v-chip>
                </div>
              </v-card>
            </v-col>
          </v-row>

          <!-- Grafico de fluxo -->
          <v-card-text v-if="!loadingComparacaoBases && (resumoComparacaoExibido.total_a > 0 || resumoComparacaoExibido.total_b > 0)">
            <apexchart
              type="bar" height="220"
              :options="dadosGraficoComparacao.options"
              :series="seriesGraficoComparacao"
            ></apexchart>
          </v-card-text>

          <!-- Tabela por assessor -->
          <v-divider></v-divider>
          <v-data-table
            :headers="headersComparacao"
            :items="assessoresComparacaoFiltrados"
            :loading="loadingComparacaoBases"
            loading-text="Comparando bases..."
            :items-per-page="15"
            class="modern-table clickable-rows"
            dense
            @click:row="abrirDrillDownComparacao"
          >
            <template v-slot:item.equipe_nome="{ item }">
              <span v-if="item.equipe_nome" class="caption">{{ item.equipe_nome }}</span>
              <span v-else class="grey--text">—</span>
            </template>
            <template v-slot:item.assessor_nome="{ item }">
              <span :class="item.id_assessor === 0 ? 'grey--text font-italic' : ''">{{ item.assessor_nome }}</span>
            </template>
            <template v-slot:item.total_a="{ item }">{{ item.total_a.toLocaleString() }}</template>
            <template v-slot:item.total_b="{ item }">{{ item.total_b.toLocaleString() }}</template>
            <template v-slot:item.repetidas="{ item }">{{ item.repetidas.toLocaleString() }}</template>
            <template v-slot:item.novas_b="{ item }">
              <span class="success--text font-weight-bold">{{ item.novas_b.toLocaleString() }}</span>
            </template>
            <template v-slot:item.pct_renovacao="{ item }">
              <v-chip v-if="item.pct_renovacao !== null" small :color="getPctRenovacaoColor(item.pct_renovacao)" dark>
                {{ item.pct_renovacao }}%
              </v-chip>
              <span v-else class="grey--text">—</span>
            </template>
            <template v-slot:item.classificacao="{ item }">
              <v-chip small :color="classificacaoColor(item.classificacao)" dark>
                {{ classificacaoLabel(item.classificacao) }}
              </v-chip>
            </template>
            <template v-slot:item.revisar_count="{ item }">
              <v-chip v-if="item.revisar_count > 0" x-small color="amber darken-2" dark>{{ item.revisar_count }}</v-chip>
              <span v-else class="grey--text">—</span>
            </template>
            <template v-slot:no-data>
              <div class="text-center pa-8 grey--text">
                Selecione duas bases diferentes para ver a comparacao por assessor
              </div>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>

    <!-- Ranking por Renovacao -->
    <v-row v-if="porAssessorComparacao.length > 0" class="mb-6">
      <v-col cols="12">
        <v-card outlined class="table-card">
          <v-card-title class="pa-4">
            <v-icon color="grey" size="22" class="mr-2">mdi-podium</v-icon>
            <span class="subtitle-1 font-weight-bold grey--text text--darken-1">
              Ranking de Assessores por Renovacao
            </span>
            <v-chip v-if="baseAInfo && baseBInfo" small class="ml-3" outlined>
              {{ baseAInfo.ano_referencia }} → {{ baseBInfo.ano_referencia }}
            </v-chip>
            <span class="caption grey--text ml-3 d-none d-md-inline">
              Score = volume de pessoas novas (50%) + atingimento da meta (30%) + % de renovacao (20%)
            </span>
          </v-card-title>
          <v-row dense class="px-4 pb-2 align-center">
            <v-col cols="12" sm="6" md="4">
              <v-autocomplete
                v-model="filtroEquipeRanking"
                :items="opcoesEquipeComparacao"
                outlined dense clearable hide-details
                label="Filtrar por equipe"
                prepend-inner-icon="mdi-account-group"
                class="styled-select"
                no-data-text="Nenhuma equipe"
              ></v-autocomplete>
            </v-col>
            <v-col cols="12" sm="6" md="4">
              <v-autocomplete
                v-model="filtroMetaRanking"
                :items="opcoesMetaRanking"
                outlined dense clearable hide-details
                label="Filtrar por meta"
                prepend-inner-icon="mdi-target"
                class="styled-select"
                no-data-text="Nenhuma meta"
              ></v-autocomplete>
            </v-col>
            <v-col cols="12" sm="6" md="4">
              <v-text-field
                v-model="buscaRanking"
                prepend-inner-icon="mdi-magnify"
                label="Buscar assessor / equipe..."
                outlined dense clearable hide-details
                class="styled-input"
              ></v-text-field>
            </v-col>
          </v-row>
          <v-data-table
            :headers="headersRankingRenov"
            :items="rankingRenovacaoFiltrado"
            :search="buscaRanking"
            :loading="loadingComparacaoBases"
            loading-text="Calculando ranking..."
            class="modern-table clickable-rows"
            :items-per-page="10"
            @click:row="abrirDrillDownComparacao"
          >
            <template v-slot:item.posicao="{ item }">
              <span v-if="item.posicao === 1">🥇</span>
              <span v-else-if="item.posicao === 2">🥈</span>
              <span v-else-if="item.posicao === 3">🥉</span>
              <span v-else>{{ item.posicao }}</span>
            </template>
            <template v-slot:item.equipe_nome="{ item }">
              <span v-if="item.equipe_nome" class="caption">{{ item.equipe_nome }}</span>
              <span v-else class="grey--text">—</span>
            </template>
            <template v-slot:item.novas_b="{ item }">
              <span class="success--text font-weight-bold">{{ item.novas_b.toLocaleString() }}</span>
            </template>
            <template v-slot:item.pct_renovacao="{ item }">
              <v-chip v-if="item.pct_renovacao !== null" x-small :color="getPctRenovacaoColor(item.pct_renovacao)" dark>{{ item.pct_renovacao }}%</v-chip>
              <span v-else class="grey--text">—</span>
              <v-chip x-small :color="classificacaoColor(item.classificacao)" dark class="ml-1">{{ classificacaoLabel(item.classificacao) }}</v-chip>
            </template>
            <template v-slot:item.pct_meta="{ item }">
              <v-chip v-if="item.pct_meta !== null && item.pct_meta !== undefined" x-small :color="metaColor(item.pct_meta)" dark>
                {{ item.pct_meta }}% <span class="ml-1">({{ item.total_b.toLocaleString() }}/{{ item.meta_valor.toLocaleString() }})</span>
              </v-chip>
              <span v-else class="grey--text caption">sem meta</span>
            </template>
            <template v-slot:item.score_renovacao="{ item }">
              <v-chip small :color="scoreColor(item.score_renovacao)" dark>{{ item.score_renovacao !== null ? item.score_renovacao : '—' }}</v-chip>
            </template>
            <template v-slot:no-data>
              <div class="text-center pa-8 grey--text">Selecione duas bases para ver o ranking</div>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>

    <!-- Pessoas que se repetem -->
    <v-row v-if="porAssessorComparacao.length > 0" class="mb-6">
      <v-col cols="12">
        <v-card outlined class="table-card pa-4">
          <div class="d-flex flex-wrap align-center" style="gap: 16px;">
            <v-icon color="grey" size="22">mdi-account-sync</v-icon>
            <div>
              <div class="subtitle-1 font-weight-bold grey--text text--darken-1">Pessoas que se repetem</div>
              <div class="caption grey--text">
                {{ resumoComparacaoExibido.repetidas.toLocaleString() }} pessoa(s) presente(s) tanto em
                {{ baseAInfo ? baseAInfo.ano_referencia : 'A' }} quanto em {{ baseBInfo ? baseBInfo.ano_referencia : 'B' }} — não foram renovadas
              </div>
            </div>
            <v-spacer></v-spacer>
            <v-btn color="primary" depressed small @click="abrirPessoasRepetidas">
              <v-icon small left>mdi-format-list-bulleted</v-icon>
              Ver lista
            </v-btn>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Dialog Drill-down Comparar Bases -->
    <v-dialog v-model="dialogDrillDownComparacao" fullscreen transition="dialog-bottom-transition">
      <v-card tile class="d-flex flex-column" style="height: 100vh; overflow: hidden;">
        <v-toolbar flat dark color="primary" dense class="flex-grow-0">
          <v-btn icon dark @click="dialogDrillDownComparacao = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <v-toolbar-title>{{ assessorDrillComparacao ? assessorDrillComparacao.assessor_nome : '' }}</v-toolbar-title>
          <v-chip v-if="baseAInfo && baseBInfo" small outlined dark class="ml-3">
            {{ baseAInfo.ano_referencia }} → {{ baseBInfo.ano_referencia }}
          </v-chip>
        </v-toolbar>

        <!-- Detalhamento do assessor (mesmo da seção) -->
        <div v-if="linhaAssessorDrill" class="px-4 pt-4 pb-2 grey lighten-4 flex-grow-0" style="flex-shrink: 0;">
          <v-card outlined color="white" class="pa-3">
            <div class="d-flex flex-wrap align-center" style="gap: 24px;">
              <div class="d-flex align-center">
                <v-icon color="primary" class="mr-2">mdi-account-tie</v-icon>
                <div>
                  <div class="caption grey--text text-uppercase">Equipe</div>
                  <div class="subtitle-2 font-weight-bold">{{ linhaAssessorDrill.equipe_nome || '—' }}</div>
                </div>
              </div>
              <v-divider vertical></v-divider>
              <div class="d-flex align-center">
                <v-icon color="primary" class="mr-2">mdi-target</v-icon>
                <div>
                  <div class="caption grey--text text-uppercase">Meta</div>
                  <div class="subtitle-2 font-weight-bold">
                    <span v-if="linhaAssessorDrill.meta_valor !== null && linhaAssessorDrill.meta_valor !== undefined">
                      {{ linhaAssessorDrill.meta_valor.toLocaleString() }} pessoas
                    </span>
                    <span v-else class="grey--text">—</span>
                    <span v-if="linhaAssessorDrill.meta_descricao" class="caption grey--text ml-1">
                      ({{ linhaAssessorDrill.meta_descricao }})
                    </span>
                  </div>
                </div>
              </div>
              <v-divider vertical></v-divider>
              <div class="d-flex align-center">
                <v-icon color="primary" class="mr-2">mdi-flag-checkered</v-icon>
                <div>
                  <div class="caption grey--text text-uppercase">Atingiu</div>
                  <v-chip v-if="linhaAssessorDrill.pct_meta !== null && linhaAssessorDrill.pct_meta !== undefined" small :color="metaColor(linhaAssessorDrill.pct_meta)" dark>
                    {{ linhaAssessorDrill.pct_meta }}%
                    <span class="ml-1 caption">({{ linhaAssessorDrill.total_b.toLocaleString() }}/{{ linhaAssessorDrill.meta_valor.toLocaleString() }})</span>
                  </v-chip>
                  <span v-else class="caption grey--text">sem meta cadastrada</span>
                </div>
              </div>
              <v-divider vertical></v-divider>
              <div class="d-flex align-center">
                <v-icon color="primary" class="mr-2">mdi-refresh</v-icon>
                <div>
                  <div class="caption grey--text text-uppercase">Renovação</div>
                  <div>
                    <v-chip v-if="linhaAssessorDrill.pct_renovacao !== null && linhaAssessorDrill.pct_renovacao !== undefined" small :color="getPctRenovacaoColor(linhaAssessorDrill.pct_renovacao)" dark>
                      {{ linhaAssessorDrill.pct_renovacao }}%
                    </v-chip>
                    <v-chip small :color="classificacaoColor(linhaAssessorDrill.classificacao)" dark class="ml-1">
                      {{ classificacaoLabel(linhaAssessorDrill.classificacao) }}
                    </v-chip>
                  </div>
                </div>
              </div>
            </div>
          </v-card>
          <div class="d-flex flex-wrap mt-3" style="gap: 8px;">
            <v-chip label small outlined>Total {{ baseAInfo ? baseAInfo.ano_referencia : 'A' }}: <strong class="ml-1">{{ linhaAssessorDrill.total_a.toLocaleString() }}</strong></v-chip>
            <v-chip label small outlined>Total {{ baseBInfo ? baseBInfo.ano_referencia : 'B' }}: <strong class="ml-1">{{ linhaAssessorDrill.total_b.toLocaleString() }}</strong></v-chip>
            <v-chip label small color="orange lighten-4">Repetidas: <strong class="ml-1">{{ linhaAssessorDrill.repetidas.toLocaleString() }}</strong></v-chip>
            <v-chip label small color="green lighten-4">Novas em B: <strong class="ml-1">{{ linhaAssessorDrill.novas_b.toLocaleString() }}</strong></v-chip>
            <v-chip label small color="grey lighten-2">Saíram de A: <strong class="ml-1">{{ linhaAssessorDrill.saiu_a.toLocaleString() }}</strong></v-chip>
            <v-chip v-if="linhaAssessorDrill.trocou_count > 0" label small color="light-blue lighten-4">Trocou de assessor: <strong class="ml-1">{{ linhaAssessorDrill.trocou_count.toLocaleString() }}</strong></v-chip>
          </div>
        </div>

        <v-tabs v-model="abaDrillComparacao" grow show-arrows class="flex-grow-0" style="flex-shrink: 0;" @change="onAbaDrillComparacaoChange">
          <v-tab>Repetidas</v-tab>
          <v-tab>Novas em B</v-tab>
          <v-tab>Sairam de A</v-tab>
          <v-tab>
            Revisar
            <v-chip v-if="revisarDoAssessor.length > 0" x-small color="amber darken-2" dark class="ml-2">
              {{ revisarDoAssessor.length }}
            </v-chip>
          </v-tab>
          <v-tab>
            Trocou de assessor
            <v-chip v-if="trocouCountDoAssessor > 0" x-small color="light-blue darken-1" dark class="ml-2">
              {{ trocouCountDoAssessor }}
            </v-chip>
          </v-tab>
        </v-tabs>
        <v-divider style="flex-shrink: 0;"></v-divider>
        <div class="pa-4 flex-grow-1" style="overflow-y: auto;">
          <!-- Abas 0-2 e 4: listas de pessoas -->
          <template v-if="abaDrillComparacao !== 3">
            <div v-if="loadingDrillComparacao" class="text-center pa-8">
              <v-progress-circular indeterminate color="primary"></v-progress-circular>
              <div class="mt-3 grey--text">Carregando pessoas...</div>
            </div>
            <template v-else>
              <v-alert v-if="abaDrillComparacao === 4" text dense type="info" class="mb-3">
                Pessoas que estavam com este assessor em A e em B estão com outro assessor — continuam ativas no sistema mas mudaram de carteira.
              </v-alert>
              <div class="d-flex align-center mb-3">
                <v-text-field
                  v-model="drillComparacaoBusca"
                  prepend-inner-icon="mdi-magnify"
                  label="Buscar por nome, CPF, telefone..."
                  outlined dense clearable hide-details
                  class="mr-3"
                  style="max-width: 360px"
                ></v-text-field>
                <span class="caption grey--text">
                  {{ drillComparacaoPessoas.length }} pessoa(s)
                </span>
              </div>
              <v-data-table
                :headers="headersDrillPessoas"
                :items="drillComparacaoPessoas"
                :search="drillComparacaoBusca"
                dense
                :items-per-page="15"
                :footer-props="{ itemsPerPageOptions: [10, 15, 25, 50] }"
              >
                <template v-slot:item.cpf="{ item }">
                  <span v-if="item.cpf">{{ item.cpf }}</span>
                  <span v-else class="error--text caption">Nao informado</span>
                </template>
                <template v-slot:item.telefone="{ item }">
                  <span v-if="item.telefone">{{ item.telefone }}</span>
                  <span v-else class="error--text caption">Nao informado</span>
                </template>
                <template v-slot:item.email="{ item }">
                  <span v-if="item.email">{{ item.email }}</span>
                  <span v-else class="grey--text caption">Nao informado</span>
                </template>
                <template v-slot:item.zona="{ item }">
                  <span v-if="item.zona">{{ item.zona }}</span>
                  <span v-else class="grey--text">—</span>
                </template>
                <template v-slot:item.secao="{ item }">
                  <span v-if="item.secao">{{ item.secao }}</span>
                  <span v-else class="grey--text">—</span>
                </template>
                <template v-slot:item.assessor_atual_nome="{ item }">
                  <v-chip x-small color="light-blue darken-1" dark>{{ item.assessor_atual_nome }}</v-chip>
                </template>
              </v-data-table>
            </template>
          </template>

          <!-- Aba 3: possiveis matches a revisar (nomes parecidos) -->
          <template v-else>
            <v-alert text dense type="info" class="mb-3">
              Pessoas que aparecem como "novas em B" mas têm nome muito parecido com alguém que "saiu de A" — provavelmente a mesma pessoa com nome digitado diferente. Confirme as que forem a mesma pessoa.
              <span v-if="!podeConfirmarRevisao" class="d-block mt-1 caption">A confirmação só fica disponível ao comparar uma base histórica com o Cadastro Atual.</span>
            </v-alert>
            <v-data-table
              :headers="headersRevisar"
              :items="revisarDoAssessor"
              dense
              :items-per-page="15"
              :footer-props="{ itemsPerPageOptions: [10, 15, 25, 50] }"
            >
              <template v-slot:item.zs_b="{ item }">
                <span class="caption">{{ item.zona_b || '—' }} / {{ item.secao_b || '—' }}</span>
              </template>
              <template v-slot:item.zs_a="{ item }">
                <span class="caption">{{ item.zona_a || '—' }} / {{ item.secao_a || '—' }}</span>
              </template>
              <template v-slot:item.similaridade="{ item }">
                <v-chip x-small dark :color="item.similaridade >= 95 ? 'success' : item.similaridade >= 90 ? 'amber darken-2' : 'orange'">
                  {{ item.similaridade }}%
                </v-chip>
              </template>
              <template v-slot:item.acao="{ item }">
                <v-btn
                  x-small color="success" depressed
                  :loading="revisarConfirmando === item"
                  :disabled="!podeConfirmarRevisao || revisarConfirmando !== null"
                  @click="confirmarRevisao(item)"
                >
                  <v-icon x-small left>mdi-check</v-icon>
                  Confirmar
                </v-btn>
              </template>
              <template v-slot:no-data>
                <div class="text-center pa-6 grey--text">Nenhum match a revisar para este assessor</div>
              </template>
            </v-data-table>
          </template>
        </div>
      </v-card>
    </v-dialog>

    <!-- Dialog Pessoas que se repetem -->
    <v-dialog v-model="dialogPessoasRepetidas" max-width="1100" scrollable>
      <v-card class="dialog-card">
        <v-card-title class="pa-4">
          <v-icon left color="primary">mdi-account-sync</v-icon>
          Pessoas que se repetem
          <v-chip v-if="baseAInfo && baseBInfo" small class="ml-3" outlined>{{ baseAInfo.ano_referencia }} → {{ baseBInfo.ano_referencia }}</v-chip>
          <v-spacer></v-spacer>
          <v-btn icon @click="dialogPessoasRepetidas = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pa-4">
          <div v-if="loadingPessoasRepetidas" class="text-center pa-8">
            <v-progress-circular indeterminate color="primary"></v-progress-circular>
            <div class="mt-3 grey--text">Carregando pessoas...</div>
          </div>
          <template v-else>
            <div class="d-flex flex-wrap align-center mb-3" style="gap: 12px;">
              <v-autocomplete
                v-model="filtroAssessorPessoasRepetidas"
                :items="opcoesAssessorComparacao"
                outlined dense clearable hide-details
                label="Filtrar por assessor"
                prepend-inner-icon="mdi-account-search"
                class="styled-select"
                style="max-width: 320px"
                no-data-text="Nenhum assessor"
              ></v-autocomplete>
              <v-text-field
                v-model="buscaPessoasRepetidas"
                prepend-inner-icon="mdi-magnify"
                label="Buscar por nome, CPF, telefone..."
                outlined dense clearable hide-details
                style="max-width: 320px"
              ></v-text-field>
              <span class="caption grey--text">{{ pessoasRepetidasFiltradas.length }} pessoa(s)</span>
            </div>
            <v-data-table
              :headers="[
                { text: 'Nome', value: 'nome_completo' },
                { text: 'Zona', value: 'zona', width: 60, align: 'center' },
                { text: 'Secao', value: 'secao', width: 70, align: 'center' },
                { text: 'Telefone', value: 'telefone', width: 130 },
                { text: 'Equipe', value: 'equipe_nome', width: 150 },
                { text: 'Assessor (em B)', value: 'assessor_nome' }
              ]"
              :items="pessoasRepetidasFiltradas"
              :search="buscaPessoasRepetidas"
              dense
              :items-per-page="20"
              :footer-props="{ itemsPerPageOptions: [10, 20, 50, 100] }"
            >
              <template v-slot:item.zona="{ item }"><span v-if="item.zona">{{ item.zona }}</span><span v-else class="grey--text">—</span></template>
              <template v-slot:item.secao="{ item }"><span v-if="item.secao">{{ item.secao }}</span><span v-else class="grey--text">—</span></template>
              <template v-slot:item.telefone="{ item }"><span v-if="item.telefone">{{ item.telefone }}</span><span v-else class="error--text caption">Nao informado</span></template>
              <template v-slot:item.equipe_nome="{ item }"><span v-if="item.equipe_nome" class="caption">{{ item.equipe_nome }}</span><span v-else class="grey--text">—</span></template>
              <template v-slot:no-data><div class="text-center pa-6 grey--text">Nenhuma pessoa repetida</div></template>
            </v-data-table>
          </template>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Dialog Importar Base -->
    <v-dialog v-model="dialogImportar" max-width="800" persistent>
      <v-card class="dialog-card">
        <v-card-title class="headline font-weight-medium pa-4">
          <v-icon left color="primary">mdi-upload</v-icon>
          Importar Base Historica
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pa-4">
          <!-- Etapa 1: Upload -->
          <div v-if="etapaImportacao === 1">
            <v-file-input
              v-model="arquivoSelecionado"
              accept=".csv,.xlsx,.xls"
              label="Selecione o arquivo CSV ou Excel"
              prepend-icon="mdi-file-excel"
              outlined
              show-size
              @change="processarArquivo"
            ></v-file-input>
            <v-alert v-if="erroArquivo" type="error" dense class="mt-2">
              {{ erroArquivo }}
            </v-alert>
          </div>

          <!-- Etapa 2: Mapeamento -->
          <div v-if="etapaImportacao === 2">
            <v-alert type="info" dense class="mb-4">
              Mapeie as colunas do arquivo para os campos do sistema
            </v-alert>

            <v-row>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="dadosImportacao.nome_base"
                  label="Nome da Base *"
                  outlined
                  dense
                  prepend-inner-icon="mdi-label"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-select
                  v-model="dadosImportacao.ano_referencia"
                  :items="anosDisponiveis"
                  label="Ano de Referencia *"
                  outlined
                  dense
                  prepend-inner-icon="mdi-calendar"
                ></v-select>
              </v-col>
            </v-row>

            <v-divider class="my-4"></v-divider>
            <div class="subtitle-2 mb-3">Dados do Assessor</div>
            <v-row dense>
              <v-col cols="12" sm="6">
                <v-select
                  v-model="mapeamento.id_assessor"
                  :items="colunasArquivo"
                  label="ID Assessor"
                  outlined
                  dense
                  clearable
                ></v-select>
              </v-col>
              <v-col cols="12" sm="6">
                <v-select
                  v-model="mapeamento.nome_assessor"
                  :items="colunasArquivo"
                  label="Nome Assessor *"
                  outlined
                  dense
                ></v-select>
              </v-col>
              <v-col cols="12" sm="6">
                <v-select
                  v-model="mapeamento.email_assessor"
                  :items="colunasArquivo"
                  label="Email Assessor"
                  outlined
                  dense
                  clearable
                ></v-select>
              </v-col>
              <v-col cols="12" sm="6">
                <v-select
                  v-model="mapeamento.cpf_assessor"
                  :items="colunasArquivo"
                  label="CPF Assessor"
                  outlined
                  dense
                  clearable
                ></v-select>
              </v-col>
            </v-row>

            <v-divider class="my-4"></v-divider>
            <div class="subtitle-2 mb-3">Dados da Pessoa</div>
            <v-row dense>
              <v-col cols="12" sm="6">
                <v-select
                  v-model="mapeamento.nome_pessoa"
                  :items="colunasArquivo"
                  label="Nome Pessoa *"
                  outlined
                  dense
                ></v-select>
              </v-col>
              <v-col cols="12" sm="6">
                <v-select
                  v-model="mapeamento.cpf_pessoa"
                  :items="colunasArquivo"
                  label="CPF Pessoa"
                  outlined
                  dense
                  clearable
                ></v-select>
              </v-col>
              <v-col cols="12" sm="6">
                <v-select
                  v-model="mapeamento.telefone_pessoa"
                  :items="colunasArquivo"
                  label="Telefone Pessoa"
                  outlined
                  dense
                  clearable
                ></v-select>
              </v-col>
              <v-col cols="12" sm="6">
                <v-select
                  v-model="mapeamento.email_pessoa"
                  :items="colunasArquivo"
                  label="Email Pessoa"
                  outlined
                  dense
                  clearable
                ></v-select>
              </v-col>
              <v-col cols="12" sm="6">
                <v-select
                  v-model="mapeamento.zona"
                  :items="colunasArquivo"
                  label="Zona"
                  outlined
                  dense
                  clearable
                ></v-select>
              </v-col>
              <v-col cols="12" sm="6">
                <v-select
                  v-model="mapeamento.secao"
                  :items="colunasArquivo"
                  label="Secao"
                  outlined
                  dense
                  clearable
                ></v-select>
              </v-col>
            </v-row>
          </div>

          <!-- Etapa 3: Preview -->
          <div v-if="etapaImportacao === 3">
            <v-alert type="success" dense class="mb-4">
              {{ dadosMapeados.length }} registros prontos para importacao
            </v-alert>
            <v-data-table
              :headers="headersPreview"
              :items="dadosMapeados.slice(0, 10)"
              dense
              class="modern-table"
              :items-per-page="5"
            >
            </v-data-table>
            <div class="caption grey--text mt-2" v-if="dadosMapeados.length > 10">
              Mostrando 10 de {{ dadosMapeados.length }} registros
            </div>
          </div>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="pa-4">
          <v-btn text @click="fecharDialogImportar">Cancelar</v-btn>
          <v-spacer></v-spacer>
          <v-btn
            v-if="etapaImportacao > 1"
            text
            @click="etapaImportacao--"
          >
            Voltar
          </v-btn>
          <v-btn
            v-if="etapaImportacao < 3"
            color="primary"
            depressed
            :disabled="!podeAvancar"
            @click="avancarEtapa"
          >
            Proximo
          </v-btn>
          <v-btn
            v-if="etapaImportacao === 3"
            color="primary"
            depressed
            :loading="importando"
            @click="confirmarImportacao"
          >
            Importar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog Gerenciar Vínculos Consolidado -->
    <v-dialog v-model="dialogVincularTodos" max-width="960" persistent>
      <v-card class="dialog-card">
        <v-card-title class="headline font-weight-medium pa-4">
          <v-icon left color="warning">mdi-link-variant-plus</v-icon>
          Gerenciar Vínculos de Assessores
          <v-spacer></v-spacer>
          <v-btn icon @click="dialogVincularTodos = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>

        <v-card-text class="pa-4 pb-2">
          <!-- Resumo de impacto -->
          <v-row dense class="mb-4">
            <v-col cols="6" sm="3">
              <div class="text-center pa-3" style="background:#fff8e1; border-radius:8px">
                <div class="display-1 font-weight-bold warning--text">
                  {{ pendentesTodos.total_assessores_pendentes }}
                </div>
                <div class="caption grey--text">sem vínculo</div>
              </div>
            </v-col>
            <v-col cols="6" sm="3">
              <div class="text-center pa-3" style="background:#fce4ec; border-radius:8px">
                <div class="display-1 font-weight-bold error--text">
                  {{ pendentesTodos.total_pessoas_bloqueadas }}
                </div>
                <div class="caption grey--text">pessoas bloqueadas</div>
              </div>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="buscaPendente"
                prepend-inner-icon="mdi-magnify"
                label="Buscar por nome ou base..."
                outlined dense clearable hide-details
              ></v-text-field>
            </v-col>
          </v-row>

          <!-- Tabela de pendentes -->
          <div v-if="loadingPendentesTodos" class="text-center pa-8">
            <v-progress-circular indeterminate color="warning"></v-progress-circular>
            <div class="caption mt-2 grey--text">Calculando impacto...</div>
          </div>

          <v-data-table
            v-else
            :headers="headersPendentes"
            :items="pendentesListaFiltrada"
            :items-per-page="10"
            no-data-text="Nenhum assessor pendente"
            class="modern-table"
          >
            <!-- Nome do assessor histórico -->
            <template v-slot:item.nome_completo="{ item }">
              <div class="subtitle-2">{{ item.nome_completo }}</div>
              <div class="caption grey--text" v-if="item.email">{{ item.email }}</div>
            </template>

            <!-- Base / Ano -->
            <template v-slot:item.base="{ item }">
              <div>{{ item.nome_base }}</div>
              <div class="caption grey--text">{{ item.ano_referencia }}</div>
            </template>

            <!-- Pessoas bloqueadas com chip colorido por criticidade -->
            <template v-slot:item.total_pessoas_bloqueadas="{ item }">
              <v-chip
                small
                :color="item.total_pessoas_bloqueadas > 50 ? 'error' : item.total_pessoas_bloqueadas > 10 ? 'warning' : 'grey lighten-2'"
                :dark="item.total_pessoas_bloqueadas > 10"
              >
                {{ item.total_pessoas_bloqueadas }}
              </v-chip>
            </template>

            <!-- Ação: botão ou select in-line -->
            <template v-slot:item.acao="{ item }">
              <div v-if="vinculacaoAtiva === item.id" style="min-width:280px">
                <v-select
                  :value="vinculacaoSelecionada[item.id]"
                  @input="val => $set(vinculacaoSelecionada, item.id, val)"
                  :items="itensSelect(item)"
                  label="Selecionar usuário"
                  outlined dense
                  item-value="id"
                  item-text="name"
                  hide-details clearable
                  style="max-width:260px"
                ></v-select>
                <div class="d-flex mt-1">
                  <v-btn
                    x-small depressed color="primary" class="mr-1"
                    :loading="salvandoVinculacaoUnica"
                    :disabled="!vinculacaoSelecionada[item.id]"
                    @click="vincularAssessorUnico(item.id, vinculacaoSelecionada[item.id])"
                  >
                    Confirmar
                  </v-btn>
                  <v-btn x-small text @click="vinculacaoAtiva = null">Cancelar</v-btn>
                </div>
              </div>
              <v-btn
                v-else
                small depressed outlined color="warning"
                @click="vinculacaoAtiva = item.id"
              >
                <v-icon left x-small>mdi-link-variant</v-icon>
                Vincular
              </v-btn>
            </template>
          </v-data-table>
        </v-card-text>

        <v-divider></v-divider>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn text @click="dialogVincularTodos = false">Fechar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog Vincular Assessores (por base — legado) -->
    <v-dialog v-model="dialogVincular" max-width="700" persistent>
      <v-card class="dialog-card">
        <v-card-title class="headline font-weight-medium pa-4">
          <v-icon left color="warning">mdi-link-variant</v-icon>
          Vincular Assessores - {{ baseVinculacao ? baseVinculacao.nome_base : '' }}
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pa-4">
          <v-alert type="info" dense class="mb-4">
            {{ assessoresPendentes.length }} assessor(es) nao identificado(s) automaticamente
          </v-alert>
          <div
            v-for="assessor in assessoresPendentes"
            :key="assessor.id"
            class="mb-4 pa-3"
            style="background: #fafafa; border-radius: 8px"
          >
            <v-row align="center">
              <v-col cols="12" sm="5">
                <div class="subtitle-2">{{ assessor.nome_completo }}</div>
                <div class="caption grey--text" v-if="assessor.email">{{ assessor.email }}</div>
                <div class="caption grey--text" v-if="assessor.id_original">ID Original: {{ assessor.id_original }}</div>
              </v-col>
              <v-col cols="12" sm="2" class="text-center">
                <v-icon>mdi-arrow-right</v-icon>
              </v-col>
              <v-col cols="12" sm="5">
                <v-select
                  v-model="vinculacoes[assessor.id]"
                  :items="usuariosAtuais"
                  label="Assessor Atual"
                  outlined
                  dense
                  item-value="id"
                  item-text="name"
                  clearable
                ></v-select>
              </v-col>
            </v-row>
          </div>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="pa-4">
          <v-btn text @click="dialogVincular = false">Cancelar</v-btn>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            depressed
            :loading="salvandoVinculacoes"
            @click="salvarVinculacoes"
          >
            Salvar Vinculacoes
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog Confirmar Exclusao -->
    <v-dialog v-model="dialogExcluir" max-width="400">
      <v-card class="dialog-card">
        <v-card-title class="headline">Confirmar Exclusao</v-card-title>
        <v-card-text>
          Tem certeza que deseja excluir a base "{{ baseExcluir ? baseExcluir.nome_base : '' }}"?
          Esta acao nao pode ser desfeita.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="dialogExcluir = false">Cancelar</v-btn>
          <v-btn color="error" depressed :loading="excluindo" @click="excluirBase">
            Excluir
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import Vue from "vue";
import VueApexCharts from "vue-apexcharts";
import AnaliseHistoricoAPI from "@/api/analiseHistorico";
import * as XLSX from "xlsx";
import Papa from "papaparse";

Vue.component("apexchart", VueApexCharts);

export default {
  name: "AnaliseHistorico",

  data() {
    return {
      // Bases
      bases: [],
      loadingBases: false,

      // Comparar Bases
      comparar: { baseA: null, baseB: null },
      loadingComparacaoBases: false,
      resumoComparacao: {
        total_a: 0, total_b: 0, repetidas: 0, novas_em_b: 0, saiu_de_a: 0, pct_renovacao_global: 0, total_revisar: 0
      },
      porAssessorComparacao: [],
      revisarComparacao: [],
      filtroAssessorComparacao: null,
      filtroEquipeComparacao: null,
      buscaRanking: "",
      filtroEquipeRanking: null,
      filtroMetaRanking: null,
      baseAInfo: null,
      baseBInfo: null,
      headersComparacao: [
        { text: "Equipe", value: "equipe_nome", width: 150 },
        { text: "Assessor", value: "assessor_nome" },
        { text: "Total A", value: "total_a", align: "end" },
        { text: "Total B", value: "total_b", align: "end" },
        { text: "Repetidas", value: "repetidas", align: "end" },
        { text: "Novas em B", value: "novas_b", align: "end" },
        { text: "% Renovacao", value: "pct_renovacao", align: "center", width: 120 },
        { text: "Classificacao", value: "classificacao", align: "center", width: 150 },
        { text: "Revisar", value: "revisar_count", align: "center", width: 90 }
      ],
      dadosGraficoComparacao: {
        options: {
          chart: { type: "bar", stacked: true, toolbar: { show: false } },
          plotOptions: { bar: { horizontal: true, barHeight: "45%" } },
          colors: ["#FF9800", "#9E9E9E", "#4CAF50", "#42A5F5"],
          dataLabels: {
            enabled: true,
            formatter: function (val) {
              return val > 0 ? val.toLocaleString() : "";
            }
          },
          xaxis: { categories: ["Base A (origem)", "Base B (destino)"] },
          legend: { position: "top", horizontalAlign: "left" },
          tooltip: { shared: true, intersect: false }
        }
      },
      // Drill-down Comparar Bases
      dialogDrillDownComparacao: false,
      abaDrillComparacao: 0,
      assessorDrillComparacao: null,
      drillComparacaoPessoas: [],
      drillComparacaoBusca: "",
      loadingDrillComparacao: false,
      revisarConfirmando: null,
      confirmandoLote: false,

      // Dialog Importar
      dialogImportar: false,
      etapaImportacao: 1,
      arquivoSelecionado: null,
      erroArquivo: null,
      colunasArquivo: [],
      dadosArquivo: [],
      dadosImportacao: {
        nome_base: "",
        ano_referencia: new Date().getFullYear()
      },
      mapeamento: {
        id_assessor: null,
        nome_assessor: null,
        email_assessor: null,
        cpf_assessor: null,
        nome_pessoa: null,
        cpf_pessoa: null,
        telefone_pessoa: null,
        email_pessoa: null,
        zona: null,
        secao: null
      },
      dadosMapeados: [],
      importando: false,

      // Dialog Vincular (por base — legado)
      dialogVincular: false,
      baseVinculacao: null,
      assessoresPendentes: [],
      usuariosAtuais: [],
      vinculacoes: {},
      salvandoVinculacoes: false,

      // Dialog Gerenciar Vinculos Consolidado
      dialogVincularTodos: false,
      pendentesTodos: { total_assessores_pendentes: 0, total_pessoas_bloqueadas: 0, assessores_pendentes: [] },
      loadingPendentesTodos: false,
      buscaPendente: "",
      vinculacaoAtiva: null,
      vinculacaoSelecionada: {},
      salvandoVinculacaoUnica: false,

      // Dialog Excluir
      dialogExcluir: false,
      baseExcluir: null,
      excluindo: false,

      // Pessoas que se repetem (dialog)
      dialogPessoasRepetidas: false,
      pessoasRepetidas: [],
      loadingPessoasRepetidas: false,
      buscaPessoasRepetidas: "",
      filtroAssessorPessoasRepetidas: null,

      // Headers
      headersBases: [
        { text: "Nome", value: "nome_base" },
        { text: "Ano", value: "ano_referencia" },
        { text: "Registros", value: "total_registros" },
        { text: "Assessores", value: "assessores" },
        { text: "Acoes", value: "actions", sortable: false, width: 100 }
      ],
      headersRankingRenov: [
        { text: "#", value: "posicao", width: 50 },
        { text: "Equipe", value: "equipe_nome", width: 150 },
        { text: "Assessor", value: "assessor_nome" },
        { text: "Novas em B", value: "novas_b", align: "end" },
        { text: "% Renovacao", value: "pct_renovacao", align: "center", width: 200 },
        { text: "Meta atingida", value: "pct_meta", align: "center", width: 180 },
        { text: "Score", value: "score_renovacao", align: "center", width: 90 }
      ],
      headersPreview: [
        { text: "Assessor", value: "nome_assessor" },
        { text: "Pessoa", value: "nome_pessoa" },
        { text: "CPF", value: "cpf_pessoa" },
        { text: "Telefone", value: "telefone_pessoa" }
      ],
      headersPendentes: [
        { text: "Assessor Histórico", value: "nome_completo" },
        { text: "Base / Ano",         value: "base",                     sortable: false },
        { text: "Pessoas Bloqueadas", value: "total_pessoas_bloqueadas",  width: 150 },
        { text: "Ação",               value: "acao",                     sortable: false, width: 320 }
      ]
    };
  },

  computed: {
    totalPendentes() {
      return this.bases.reduce((acc, b) => acc + b.assessores_nao_identificados, 0);
    },
    basesParaComparar() {
      const lista = this.bases.map(b => ({ value: b.id, text: `${b.nome_base} (${b.ano_referencia})` }));
      lista.push({ value: "atual", text: `Cadastro Atual (${new Date().getFullYear()})` });
      return lista;
    },
    revisarDoAssessor() {
      if (!this.assessorDrillComparacao) return [];
      return this.revisarComparacao.filter(r => r.id_assessor === this.assessorDrillComparacao.id_assessor);
    },
    linhaAssessorDrill() {
      if (!this.assessorDrillComparacao) return null;
      return this.porAssessorComparacao.find(x => x.id_assessor === this.assessorDrillComparacao.id_assessor) || null;
    },
    trocouCountDoAssessor() {
      return this.linhaAssessorDrill ? (this.linhaAssessorDrill.trocou_count || 0) : 0;
    },
    // Lista (já filtrada por equipe) na qual o autocomplete de assessor opera
    porAssessorNaEquipe() {
      if (this.filtroEquipeComparacao === null) return this.porAssessorComparacao;
      return this.porAssessorComparacao.filter(a => (a.equipe_nome || null) === this.filtroEquipeComparacao);
    },
    detalheExibido() {
      // Detalhamento mostrado no painel: assessor especifico, agregado da equipe ou nada
      if (this.filtroAssessorComparacao !== null) {
        const a = this.porAssessorComparacao.find(x => x.id_assessor === this.filtroAssessorComparacao);
        return a ? { tipo: "assessor", ...a } : null;
      }
      if (this.filtroEquipeComparacao !== null) {
        const linhas = this.porAssessorNaEquipe;
        const ag = this.agregarLinhasComparacao(linhas);
        return { tipo: "equipe", equipe_nome: this.filtroEquipeComparacao, n_assessores: linhas.length, ...ag };
      }
      return null;
    },
    headersDrillPessoas() {
      const cols = [
        { text: "Nome", value: "nome_completo" },
        { text: "CPF", value: "cpf", width: 130 },
        { text: "Telefone", value: "telefone", width: 130 },
        { text: "Email", value: "email" },
        { text: "Zona", value: "zona", width: 60, align: "center" },
        { text: "Secao", value: "secao", width: 70, align: "center" }
      ];
      if (this.abaDrillComparacao === 4) {
        cols.push({ text: "Assessor atual", value: "assessor_atual_nome", width: 200 });
      }
      return cols;
    },
    podeConfirmarRevisao() {
      return !!(this.baseAInfo && this.baseBInfo) &&
        (this.baseAInfo.id === "atual" || this.baseBInfo.id === "atual");
    },
    headersRevisar() {
      const anoB = this.baseBInfo ? this.baseBInfo.ano_referencia : "destino";
      const anoA = this.baseAInfo ? this.baseAInfo.ano_referencia : "origem";
      const cols = [
        { text: `Nome em B (${anoB})`, value: "nome_b" },
        { text: "Zona/Secao B", value: "zs_b", align: "center", width: 110, sortable: false },
        { text: `Nome em A (${anoA})`, value: "nome_a" },
        { text: "Zona/Secao A", value: "zs_a", align: "center", width: 110, sortable: false },
        { text: "Similaridade", value: "similaridade", align: "center", width: 120 }
      ];
      if (this.podeConfirmarRevisao) {
        cols.push({ text: "Ação", value: "acao", align: "center", width: 140, sortable: false });
      }
      return cols;
    },
    opcoesEquipeComparacao() {
      const nomes = Array.from(new Set(
        this.porAssessorComparacao.map(a => a.equipe_nome).filter(n => !!n)
      )).sort((x, y) => x.localeCompare(y));
      return [{ text: "Todas as equipes", value: null }, ...nomes.map(n => ({ text: n, value: n }))];
    },
    opcoesAssessorComparacao() {
      const lista = this.porAssessorNaEquipe
        .map(a => ({ text: a.assessor_nome, value: a.id_assessor }))
        .sort((x, y) => x.text.localeCompare(y.text));
      return [{ text: "Todos os assessores", value: null }, ...lista];
    },
    assessoresComparacaoFiltrados() {
      let lista = this.porAssessorNaEquipe;
      if (this.filtroAssessorComparacao !== null) {
        lista = lista.filter(a => a.id_assessor === this.filtroAssessorComparacao);
      }
      return lista;
    },
    nomeAssessorFiltrado() {
      const a = this.porAssessorComparacao.find(x => x.id_assessor === this.filtroAssessorComparacao);
      return a ? a.assessor_nome : "";
    },
    // Conjunto de id_assessor que está no escopo atual (respeitando filtros de equipe/assessor)
    idsAssessorNoEscopo() {
      if (this.filtroAssessorComparacao !== null) return new Set([this.filtroAssessorComparacao]);
      return new Set(this.porAssessorNaEquipe.map(a => a.id_assessor));
    },
    revisarAltaNoEscopo() {
      const ids = this.idsAssessorNoEscopo;
      return this.revisarComparacao.filter(r => r.similaridade >= 95 && ids.has(r.id_assessor));
    },
    resumoComparacaoExibido() {
      const d = this.detalheExibido;
      if (d) {
        return {
          total_a: d.total_a,
          total_b: d.total_b,
          repetidas: d.repetidas,
          novas_em_b: d.novas_b,
          saiu_de_a: d.saiu_a,
          pct_renovacao_global: d.pct_renovacao === null || d.pct_renovacao === undefined ? 0 : d.pct_renovacao,
          total_revisar: d.revisar_count || 0,
          trocou: d.trocou_count || 0
        };
      }
      return { ...this.resumoComparacao, trocou: 0 };
    },
    seriesGraficoComparacao() {
      const r = this.resumoComparacaoExibido;
      // Barra A = repetidas + saíram (+ trocou, quando filtrado por assessor).
      // Barra B = repetidas + novas.
      const series = [
        { name: "Repetidas (A e B)", data: [r.repetidas, r.repetidas] },
        { name: "Sairam de A", data: [r.saiu_de_a, 0] },
        { name: "Novas em B", data: [0, r.novas_em_b] }
      ];
      if (r.trocou > 0) {
        series.push({ name: "Trocou de assessor", data: [r.trocou, 0] });
      }
      return series;
    },
    totalPessoasBloqueadas() {
      return this.pendentesTodos.total_pessoas_bloqueadas || 0;
    },
    pendentesListaFiltrada() {
      const lista = this.pendentesTodos.assessores_pendentes || [];
      if (!this.buscaPendente) return lista;
      const termo = this.buscaPendente.toLowerCase();
      return lista.filter(a =>
        a.nome_completo.toLowerCase().includes(termo) ||
        (a.nome_base && a.nome_base.toLowerCase().includes(termo))
      );
    },
    anosDisponiveis() {
      const anoAtual = new Date().getFullYear();
      const anos = [];
      for (let i = anoAtual; i >= 2015; i--) {
        anos.push(i);
      }
      return anos;
    },
    podeAvancar() {
      if (this.etapaImportacao === 1) {
        return this.dadosArquivo.length > 0;
      }
      if (this.etapaImportacao === 2) {
        return (
          this.dadosImportacao.nome_base &&
          this.dadosImportacao.ano_referencia &&
          this.mapeamento.nome_assessor &&
          this.mapeamento.nome_pessoa
        );
      }
      return true;
    },
    // Ranking dos assessores vinculados por score de renovacao (das bases A→B)
    rankingRenovacao() {
      const sc = a => (a.score_renovacao === null || a.score_renovacao === undefined ? -1 : a.score_renovacao);
      return this.porAssessorComparacao
        .filter(a => a.id_assessor > 0)
        .slice()
        .sort((x, y) => sc(y) - sc(x))
        .map((a, i) => ({ ...a, posicao: i + 1 }));
    },
    opcoesMetaRanking() {
      const metas = Array.from(new Set(
        this.porAssessorComparacao
          .filter(a => a.id_assessor > 0 && a.meta_descricao)
          .map(a => a.meta_descricao)
      )).sort((x, y) => x.localeCompare(y));
      return [{ text: "Todas as metas", value: null }, ...metas.map(m => ({ text: m, value: m }))];
    },
    rankingRenovacaoFiltrado() {
      return this.rankingRenovacao.filter(a => {
        if (this.filtroEquipeRanking !== null && (a.equipe_nome || null) !== this.filtroEquipeRanking) return false;
        if (this.filtroMetaRanking !== null && (a.meta_descricao || null) !== this.filtroMetaRanking) return false;
        return true;
      });
    },
    pessoasRepetidasFiltradas() {
      if (this.filtroAssessorPessoasRepetidas === null) return this.pessoasRepetidas;
      return this.pessoasRepetidas.filter(p => p.id_assessor === this.filtroAssessorPessoasRepetidas);
    }
  },

  created() {
    this.Helpers.verificarToken();
    this.carregarDados();
  },

  methods: {
    async carregarDados() {
      this.carregarBases();
    },

    async carregarBases() {
      this.loadingBases = true;
      try {
        const headers = this.Helpers.getHeaderRequisicao();
        const response = await AnaliseHistoricoAPI.getBases(headers);
        localStorage.setItem("token", response.data.token);
        this.bases = response.data.bases;

        // Pré-selecionar bases para comparacao (A = base historica mais recente, B = cadastro atual)
        if (this.bases.length >= 1 && !this.comparar.baseA && !this.comparar.baseB) {
          this.comparar.baseA = this.bases[0].id;
          this.comparar.baseB = "atual";
          this.carregarComparacaoBases();
        }

        // Pré-carregar impacto de bloqueados para o card de alerta
        if (this.bases.some(b => b.assessores_nao_identificados > 0)) {
          this.carregarPendentesTodos();
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Erro ao carregar bases:", error);
      } finally {
        this.loadingBases = false;
      }
    },

    async carregarComparacaoBases() {
      const { baseA, baseB } = this.comparar;
      if (!baseA || !baseB || baseA === baseB) return;

      this.loadingComparacaoBases = true;
      this.filtroAssessorComparacao = null;
      this.filtroEquipeComparacao = null;
      this.buscaRanking = "";
      this.filtroEquipeRanking = null;
      this.filtroMetaRanking = null;
      this.pessoasRepetidas = [];
      this.dialogPessoasRepetidas = false;
      this.filtroAssessorPessoasRepetidas = null;
      this.buscaPessoasRepetidas = "";
      try {
        const headers = this.Helpers.getHeaderRequisicao();
        const response = await AnaliseHistoricoAPI.compararBases(headers, baseA, baseB);
        localStorage.setItem("token", response.data.token);

        const r = response.data.resumo_global || {};
        this.resumoComparacao = {
          total_a: r.total_a || 0,
          total_b: r.total_b || 0,
          repetidas: r.repetidas || 0,
          novas_em_b: r.novas_em_b || 0,
          saiu_de_a: r.saiu_de_a || 0,
          pct_renovacao_global: r.pct_renovacao_global || 0,
          total_revisar: r.total_revisar || 0
        };
        this.porAssessorComparacao = response.data.por_assessor || [];
        this.revisarComparacao = response.data.revisar || [];
        this.baseAInfo = response.data.base_a || null;
        this.baseBInfo = response.data.base_b || null;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Erro ao comparar bases:", error);
      } finally {
        this.loadingComparacaoBases = false;
      }
    },

    inverterBases() {
      const a = this.comparar.baseA;
      this.comparar.baseA = this.comparar.baseB;
      this.comparar.baseB = a;
      this.carregarComparacaoBases();
    },

    getPctRenovacaoColor(pct) {
      if (pct === null || pct === undefined) return "grey";
      if (pct >= 50) return "success";
      if (pct >= 20) return "info";
      return "warning";
    },

    classificacaoLabel(c) {
      return {
        renovou_bem: "Renovou bem",
        renovou_pouco: "Renovou pouco",
        estagnou: "Estagnou",
        encolheu: "Encolheu"
      }[c] || c;
    },

    classificacaoColor(c) {
      return {
        renovou_bem: "success",
        renovou_pouco: "info",
        estagnou: "warning",
        encolheu: "error"
      }[c] || "grey";
    },

    metaColor(pct) {
      if (pct === null || pct === undefined) return "grey";
      if (pct >= 100) return "success";
      if (pct >= 80) return "info";
      if (pct >= 50) return "warning";
      return "error";
    },

    classificarRenovacaoJs(totalB, pct) {
      if (totalB === 0) return "encolheu";
      if (pct >= 50) return "renovou_bem";
      if (pct >= 20) return "renovou_pouco";
      return "estagnou";
    },

    agregarLinhasComparacao(linhas) {
      const ag = {
        total_a: 0, total_b: 0, repetidas: 0, novas_b: 0, saiu_a: 0,
        trocou_count: 0, revisar_count: 0, meta_valor: 0
      };
      let temMeta = false;
      linhas.forEach(l => {
        ag.total_a += l.total_a || 0;
        ag.total_b += l.total_b || 0;
        ag.repetidas += l.repetidas || 0;
        ag.novas_b += l.novas_b || 0;
        ag.saiu_a += l.saiu_a || 0;
        ag.trocou_count += l.trocou_count || 0;
        ag.revisar_count += l.revisar_count || 0;
        if (l.meta_valor !== null && l.meta_valor !== undefined) {
          ag.meta_valor += l.meta_valor;
          temMeta = true;
        }
      });
      ag.pct_renovacao = ag.total_b > 0 ? Math.round((ag.novas_b / ag.total_b) * 1000) / 10 : null;
      ag.classificacao = this.classificarRenovacaoJs(ag.total_b, ag.pct_renovacao === null ? 0 : ag.pct_renovacao);
      ag.meta_valor = temMeta ? ag.meta_valor : null;
      ag.meta_descricao = null;
      ag.pct_meta = (ag.meta_valor && ag.meta_valor > 0) ? Math.round((ag.total_b / ag.meta_valor) * 1000) / 10 : null;
      return ag;
    },

    // Atualiza o estado local apos confirmar um par: deixa de ser "nova/saiu" e vira "repetida"
    aplicarConfirmacaoLocal(item) {
      const idx = this.revisarComparacao.indexOf(item);
      if (idx >= 0) this.revisarComparacao.splice(idx, 1);

      const linha = this.porAssessorComparacao.find(a => a.id_assessor === item.id_assessor);
      if (linha) {
        linha.repetidas += 1;
        linha.novas_b = Math.max(0, linha.novas_b - 1);
        linha.saiu_a = Math.max(0, linha.saiu_a - 1);
        linha.revisar_count = Math.max(0, (linha.revisar_count || 0) - 1);
        linha.pct_renovacao = linha.total_b > 0 ? Math.round((linha.novas_b / linha.total_b) * 1000) / 10 : null;
        linha.classificacao = this.classificarRenovacaoJs(linha.total_b, linha.pct_renovacao === null ? 0 : linha.pct_renovacao);
      }

      this.resumoComparacao.repetidas += 1;
      this.resumoComparacao.novas_em_b = Math.max(0, this.resumoComparacao.novas_em_b - 1);
      this.resumoComparacao.saiu_de_a = Math.max(0, this.resumoComparacao.saiu_de_a - 1);
      this.resumoComparacao.total_revisar = Math.max(0, (this.resumoComparacao.total_revisar || 0) - 1);
      this.resumoComparacao.pct_renovacao_global = this.resumoComparacao.total_b > 0
        ? Math.round((this.resumoComparacao.novas_em_b / this.resumoComparacao.total_b) * 1000) / 10
        : 0;
    },

    async confirmarRevisao(item) {
      if (!this.podeConfirmarRevisao || this.revisarConfirmando !== null) return;
      this.revisarConfirmando = item;
      try {
        const headers = this.Helpers.getHeaderRequisicao();
        const response = await AnaliseHistoricoAPI.confirmarMatchRevisao(headers, {
          tipo_a: item.tipo_a,
          tipo_b: item.tipo_b,
          id_ref_a: item.id_ref_a,
          id_ref_b: item.id_ref_b
        });
        localStorage.setItem("token", response.data.token);
        this.aplicarConfirmacaoLocal(item);
        this.$store.dispatch("snackbar/showSnackbar", {
          message: "Confirmado — o par passa a contar como pessoa repetida.",
          color: "success"
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Erro ao confirmar match:", error);
        const msg = error.response && error.response.data && error.response.data.message
          ? error.response.data.message
          : "Erro ao confirmar o match.";
        this.$store.dispatch("snackbar/showSnackbar", { message: msg, color: "error" });
      } finally {
        this.revisarConfirmando = null;
      }
    },

    async confirmarRevisaoLote() {
      if (!this.podeConfirmarRevisao || this.confirmandoLote) return;
      const itens = this.revisarAltaNoEscopo;
      if (itens.length === 0) return;
      this.confirmandoLote = true;
      try {
        const headers = this.Helpers.getHeaderRequisicao();
        const pares = itens.map(r => ({
          tipo_a: r.tipo_a, tipo_b: r.tipo_b, id_ref_a: r.id_ref_a, id_ref_b: r.id_ref_b
        }));
        const response = await AnaliseHistoricoAPI.confirmarMatchRevisaoLote(headers, { pares });
        localStorage.setItem("token", response.data.token);
        // aplica localmente para todos (cobre o caso comum em que todos foram confirmados)
        itens.slice().forEach(it => this.aplicarConfirmacaoLocal(it));
        const conf = response.data.confirmados || 0;
        const erros = response.data.erros || 0;
        this.$store.dispatch("snackbar/showSnackbar", {
          message: `${conf} par(es) confirmado(s)${erros ? ` (${erros} com erro)` : ""}.`,
          color: erros ? "warning" : "success"
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Erro ao confirmar em lote:", error);
        this.$store.dispatch("snackbar/showSnackbar", { message: "Erro ao confirmar os pares.", color: "error" });
      } finally {
        this.confirmandoLote = false;
      }
    },

    abrirDrillDownComparacao(item) {
      this.assessorDrillComparacao = { id_assessor: item.id_assessor, assessor_nome: item.assessor_nome };
      this.dialogDrillDownComparacao = true;
      this.abaDrillComparacao = 0;
      this.carregarDrillComparacao("repetidas");
    },

    onAbaDrillComparacaoChange() {
      // aba 3 = "Revisar" -> renderizada localmente, sem chamada de API
      if (this.abaDrillComparacao === 3) return;
      const tipos = ["repetidas", "novas", "saiu", null, "trocou"];
      const tipo = tipos[this.abaDrillComparacao] || "repetidas";
      this.carregarDrillComparacao(tipo);
    },

    async carregarDrillComparacao(tipo) {
      if (!this.assessorDrillComparacao) return;
      this.loadingDrillComparacao = true;
      this.drillComparacaoPessoas = [];
      this.drillComparacaoBusca = "";
      try {
        const headers = this.Helpers.getHeaderRequisicao();
        const response = await AnaliseHistoricoAPI.compararBasesPessoas(
          headers,
          this.assessorDrillComparacao.id_assessor,
          this.comparar.baseA,
          this.comparar.baseB,
          tipo
        );
        localStorage.setItem("token", response.data.token);
        this.drillComparacaoPessoas = response.data.pessoas || [];
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Erro ao carregar drill-down de comparacao:", error);
      } finally {
        this.loadingDrillComparacao = false;
      }
    },

    processarArquivo(file) {
      if (!file) {
        this.dadosArquivo = [];
        this.colunasArquivo = [];
        return;
      }

      this.erroArquivo = null;
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const extension = file.name.split(".").pop().toLowerCase();

          if (extension === "csv") {
            Papa.parse(e.target.result, {
              header: true,
              skipEmptyLines: true,
              complete: (results) => {
                this.dadosArquivo = results.data;
                const fields = results.meta.fields || [];
                this.colunasArquivo = fields.map((field) => ({
                  text: field,
                  value: field
                }));
              }
            });
          } else {
            const workbook = XLSX.read(e.target.result, { type: "binary" });
            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];
            this.dadosArquivo = XLSX.utils.sheet_to_json(worksheet);
            if (this.dadosArquivo.length > 0) {
              const keys = Object.keys(this.dadosArquivo[0]);
              this.colunasArquivo = keys.map((key) => ({
                text: key,
                value: key
              }));
            }
          }

          // Sugerir nome da base baseado no nome do arquivo
          this.dadosImportacao.nome_base = file.name.replace(/\.[^/.]+$/, "");
        } catch (error) {
          this.erroArquivo = "Erro ao processar arquivo: " + error.message;
        }
      };

      reader.onerror = () => {
        this.erroArquivo = "Erro ao ler arquivo";
      };

      const extension = file.name.split(".").pop().toLowerCase();
      if (extension === "csv") {
        reader.readAsText(file);
      } else {
        reader.readAsBinaryString(file);
      }
    },

    avancarEtapa() {
      if (this.etapaImportacao === 2) {
        // Mapear dados
        this.dadosMapeados = this.dadosArquivo.map(row => ({
          id_assessor_original: this.mapeamento.id_assessor ? row[this.mapeamento.id_assessor] : null,
          nome_assessor: row[this.mapeamento.nome_assessor] || "",
          email_assessor: this.mapeamento.email_assessor ? row[this.mapeamento.email_assessor] : null,
          cpf_assessor: this.mapeamento.cpf_assessor ? row[this.mapeamento.cpf_assessor] : null,
          id_pessoa_original: null,
          nome_pessoa: row[this.mapeamento.nome_pessoa] || "",
          cpf_pessoa: this.mapeamento.cpf_pessoa ? row[this.mapeamento.cpf_pessoa] : null,
          telefone_pessoa: this.mapeamento.telefone_pessoa ? row[this.mapeamento.telefone_pessoa] : null,
          email_pessoa: this.mapeamento.email_pessoa ? row[this.mapeamento.email_pessoa] : null,
          zona: this.mapeamento.zona ? row[this.mapeamento.zona] : null,
          secao: this.mapeamento.secao ? row[this.mapeamento.secao] : null
        })).filter(r => r.nome_assessor && r.nome_pessoa);
      }
      this.etapaImportacao++;
    },

    async confirmarImportacao() {
      this.importando = true;
      try {
        const headers = this.Helpers.getHeaderRequisicao();

        // Backend usa índices numéricos (1-based); converter nomes de colunas para índices
        const mapeamentoComIndices = {};
        for (const [campo, nomeColuna] of Object.entries(this.mapeamento)) {
          if (nomeColuna !== null && nomeColuna !== undefined) {
            const idx = this.colunasArquivo.findIndex(c => c.value === nomeColuna) + 1;
            mapeamentoComIndices[campo] = idx > 0 ? idx : null;
          } else {
            mapeamentoComIndices[campo] = null;
          }
        }

        const response = await AnaliseHistoricoAPI.importarArquivo(
          headers,
          this.arquivoSelecionado,
          this.dadosImportacao.nome_base,
          this.dadosImportacao.ano_referencia,
          mapeamentoComIndices
        );

        localStorage.setItem("token", response.data.token);

        this.$store.dispatch("snackbar/showSnackbar", {
          message: "Base importada com sucesso!",
          color: "success"
        });

        this.fecharDialogImportar();
        this.carregarDados();
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Erro ao importar:", error);
        const errorMessage = error.response && error.response.data && error.response.data.message
          ? error.response.data.message
          : "Erro ao importar base";
        this.$store.dispatch("snackbar/showSnackbar", {
          message: errorMessage,
          color: "error"
        });
      } finally {
        this.importando = false;
      }
    },

    fecharDialogImportar() {
      this.dialogImportar = false;
      this.etapaImportacao = 1;
      this.arquivoSelecionado = null;
      this.dadosArquivo = [];
      this.colunasArquivo = [];
      this.dadosMapeados = [];
      this.mapeamento = {
        id_assessor: null,
        nome_assessor: null,
        email_assessor: null,
        cpf_assessor: null,
        nome_pessoa: null,
        cpf_pessoa: null,
        telefone_pessoa: null,
        email_pessoa: null,
        zona: null,
        secao: null
      };
    },

    // --- Gerenciar Vínculos Consolidado ---

    async abrirGerenciarVinculos() {
      this.dialogVincularTodos = true;
      this.buscaPendente = "";
      this.vinculacaoAtiva = null;
      this.vinculacaoSelecionada = {};
      await Promise.all([this.carregarPendentesTodos(), this.carregarUsuariosAtuais()]);
    },

    async carregarPendentesTodos() {
      this.loadingPendentesTodos = true;
      try {
        const headers = this.Helpers.getHeaderRequisicao();
        const response = await AnaliseHistoricoAPI.getAssessoresPendentesTodos(headers);
        localStorage.setItem("token", response.data.token);
        this.pendentesTodos = {
          total_assessores_pendentes: response.data.total_assessores_pendentes,
          total_pessoas_bloqueadas:   response.data.total_pessoas_bloqueadas,
          assessores_pendentes:       response.data.assessores_pendentes
        };
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Erro ao carregar pendentes consolidados:", error);
      } finally {
        this.loadingPendentesTodos = false;
      }
    },

    async carregarUsuariosAtuais() {
      if (this.usuariosAtuais.length > 0) return;
      const primeiraBase = this.bases && this.bases[0];
      if (!primeiraBase) return;
      try {
        const headers = this.Helpers.getHeaderRequisicao();
        const response = await AnaliseHistoricoAPI.getAssessoresPendentes(headers, primeiraBase.id);
        localStorage.setItem("token", response.data.token);
        this.usuariosAtuais = response.data.usuarios_atuais;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Erro ao carregar usuarios:", error);
      }
    },

    async vincularAssessorUnico(idAssessorHistorico, idUsuarioAtual) {
      if (!idUsuarioAtual) return;
      this.salvandoVinculacaoUnica = true;
      try {
        const headers = this.Helpers.getHeaderRequisicao();
        const response = await AnaliseHistoricoAPI.vincularAssessor(headers, {
          id_assessor_historico: idAssessorHistorico,
          id_usuario_atual: idUsuarioAtual
        });
        localStorage.setItem("token", response.data.token);

        const item = (this.pendentesTodos.assessores_pendentes || []).find(
          a => a.id === idAssessorHistorico
        );
        const qtd = item ? item.total_pessoas_bloqueadas : 0;

        this.pendentesTodos.assessores_pendentes = this.pendentesTodos.assessores_pendentes.filter(
          a => a.id !== idAssessorHistorico
        );
        this.pendentesTodos.total_assessores_pendentes -= 1;
        this.pendentesTodos.total_pessoas_bloqueadas -= qtd;

        this.vinculacaoAtiva = null;
        this.$delete(this.vinculacaoSelecionada, idAssessorHistorico);

        this.$store.dispatch("snackbar/showSnackbar", {
          message: qtd > 0
            ? qtd + " pessoa(s) desbloqueada(s) com sucesso!"
            : "Assessor vinculado com sucesso!",
          color: "success"
        });

        this.carregarBases();
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Erro ao vincular:", error);
        this.$store.dispatch("snackbar/showSnackbar", {
          message: "Erro ao vincular assessor",
          color: "error"
        });
      } finally {
        this.salvandoVinculacaoUnica = false;
      }
    },

    itensSelect(assessor) {
      if (!this.usuariosAtuais || this.usuariosAtuais.length === 0) return [];
      const idsSugestoes = assessor.sugestoes.map(s => s.id);
      const outros = this.usuariosAtuais.filter(u => !idsSugestoes.includes(u.id));
      const sugestoesFormatadas = assessor.sugestoes.map(s => ({
        id: s.id,
        name: "★ " + s.name + " (" + s.similaridade + "%)",
        isSugestao: true
      }));
      if (sugestoesFormatadas.length > 0 && outros.length > 0) {
        return [...sugestoesFormatadas, { divider: true }, ...outros];
      }
      return [...sugestoesFormatadas, ...outros];
    },

    // --- Fim Gerenciar Vínculos Consolidado ---

    async abrirVinculacao(base) {
      this.baseVinculacao = base;
      this.vinculacoes = {};

      try {
        const headers = this.Helpers.getHeaderRequisicao();
        const response = await AnaliseHistoricoAPI.getAssessoresPendentes(headers, base.id);
        localStorage.setItem("token", response.data.token);

        this.assessoresPendentes = response.data.assessores_pendentes;
        this.usuariosAtuais = response.data.usuarios_atuais;
        this.dialogVincular = true;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Erro ao carregar pendentes:", error);
      }
    },

    async salvarVinculacoes() {
      this.salvandoVinculacoes = true;
      try {
        const headers = this.Helpers.getHeaderRequisicao();

        for (const [idHistorico, idAtual] of Object.entries(this.vinculacoes)) {
          if (idAtual) {
            await AnaliseHistoricoAPI.vincularAssessor(headers, {
              id_assessor_historico: parseInt(idHistorico),
              id_usuario_atual: idAtual
            });
          }
        }

        this.$store.dispatch("snackbar/showSnackbar", {
          message: "Vinculacoes salvas com sucesso!",
          color: "success"
        });

        this.dialogVincular = false;
        this.carregarDados();
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Erro ao salvar vinculacoes:", error);
        this.$store.dispatch("snackbar/showSnackbar", {
          message: "Erro ao salvar vinculacoes",
          color: "error"
        });
      } finally {
        this.salvandoVinculacoes = false;
      }
    },

    confirmarExclusao(base) {
      this.baseExcluir = base;
      this.dialogExcluir = true;
    },

    async excluirBase() {
      this.excluindo = true;
      try {
        const headers = this.Helpers.getHeaderRequisicao();
        await AnaliseHistoricoAPI.delBase(headers, this.baseExcluir.id);

        this.$store.dispatch("snackbar/showSnackbar", {
          message: "Base excluida com sucesso!",
          color: "success"
        });

        this.dialogExcluir = false;
        this.carregarDados();
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Erro ao excluir:", error);
        this.$store.dispatch("snackbar/showSnackbar", {
          message: "Erro ao excluir base",
          color: "error"
        });
      } finally {
        this.excluindo = false;
      }
    },

    scoreColor(score) {
      if (score === null || score === undefined) return "grey";
      if (score >= 70) return "success";
      if (score >= 40) return "info";
      return "warning";
    },

    abrirPessoasRepetidas() {
      this.dialogPessoasRepetidas = true;
      if (this.pessoasRepetidas.length === 0 && !this.loadingPessoasRepetidas) {
        this.carregarPessoasRepetidas();
      }
    },

    async carregarPessoasRepetidas() {
      const { baseA, baseB } = this.comparar;
      if (!baseA || !baseB || baseA === baseB) return;
      this.loadingPessoasRepetidas = true;
      try {
        const headers = this.Helpers.getHeaderRequisicao();
        const response = await AnaliseHistoricoAPI.compararBasesPessoasGlobal(headers, baseA, baseB, "repetidas");
        localStorage.setItem("token", response.data.token);
        this.pessoasRepetidas = response.data.pessoas || [];
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Erro ao carregar pessoas repetidas:", error);
      } finally {
        this.loadingPessoasRepetidas = false;
      }
    }
  }
};
</script>

<style scoped>
.analise-page {
  background-color: #f5f7fb;
  min-height: 100vh;
}

.kpi-card {
  border-radius: 16px;
  border-color: rgba(0, 0, 0, 0.08);
  transition: box-shadow 0.2s;
}

.kpi-card:hover {
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.table-card {
  border-radius: 16px;
  border-color: rgba(0, 0, 0, 0.08);
}

.dialog-card {
  border-radius: 16px;
}

.modern-table {
  border-radius: 0 0 16px 16px;
}

.btn-novo {
  border-radius: 12px;
  text-transform: none;
  font-weight: 600;
  letter-spacing: 0;
}

.action-icon-btn {
  transition: transform 0.2s, background-color 0.2s;
}

.action-icon-btn:hover {
  transform: scale(1.1);
  background-color: rgba(0, 0, 0, 0.04);
}

.styled-select >>> .v-input__slot {
  border-radius: 12px !important;
}

.styled-input >>> .v-input__slot {
  border-radius: 12px !important;
}

.clickable-rows >>> tbody tr {
  cursor: pointer;
}

.clickable-rows >>> tbody tr:hover {
  background-color: #f5f5f5 !important;
}
</style>
